import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Play, Grid, FileSpreadsheet, Plus, HelpCircle } from 'lucide-react';
import { ATELIER, FONTS } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';

/**
 * Parses cell coordinates like 'C4' or 'D12' to row/col indexes.
 * Column Letter (e.g. A, B, C...) maps to 0, 1, 2...
 * Row number (1-based index) maps to row index.
 */
function parseCellRef(ref) {
  if (!ref || typeof ref !== 'string') return null;
  const match = ref.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return null;
  const colStr = match[1].toUpperCase();
  const rowNum = parseInt(match[2]) - 1; // 0-indexed row for grid

  // Convert column letter to 0-indexed column offset (A=0, B=1, AA=26, etc)
  let colIndex = 0;
  for (let i = 0; i < colStr.length; i++) {
    colIndex = colIndex * 26 + (colStr.charCodeAt(i) - 64);
  }
}

/** Converts column index (0-based) to letter (A, B, C...) */
function colLetter(index) {
  let temp = index;
  let letter = '';
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

/* ── DownloadDropdown Component ─────────────────────────────── */
function DownloadDropdown({ handleDownloadExcel, sheets, workbookTitle, activeSheetIndex }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadCSV = () => {
    const currentSheet = sheets[activeSheetIndex];
    if (!currentSheet) return;
    const { headers, rows } = currentSheet;
    
    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(val => {
        const strVal = String(val).replace(/"/g, '""');
        return strVal.includes(',') || strVal.includes('\n') ? `"${strVal}"` : strVal;
      }).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentSheet.name}_Export_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleDownloadWord = () => {
    let htmlContent = '';
    sheets.forEach(sh => {
      htmlContent += `<h2>Sheet: ${sh.name}</h2>`;
      htmlContent += `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; margin-bottom: 30px; font-family: Segoe UI, sans-serif; font-size: 13px;">`;
      
      // Headers
      htmlContent += `<tr style="background: #10B981; color: white; font-weight: bold;">`;
      sh.headers.forEach(h => {
        htmlContent += `<th>${h}</th>`;
      });
      htmlContent += `</tr>`;

      // Rows
      sh.rows.forEach(row => {
        htmlContent += `<tr>`;
        row.forEach(cell => {
          htmlContent += `<td>${cell || ''}</td>`;
        });
        htmlContent += `</tr>`;
      });
      htmlContent += `</table>`;
    });

    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <title>${workbookTitle}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #1e293b; }
          h1 { color: #10B981; font-size: 22px; margin-bottom: 24px; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px; }
          h2 { color: #0f172a; font-size: 16px; margin-top: 20px; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <h1>${workbookTitle}</h1>
        ${htmlContent}
      </body>
      </html>
    `;

    const blob = new Blob([docContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${workbookTitle.replace(/\s+/g, '_')}_WordExport_${Date.now()}.doc`;
    link.click();
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    let tablesContent = '';
    sheets.forEach(sh => {
      tablesContent += `
        <div style="page-break-inside: avoid; margin-bottom: 40px;">
          <h2 style="font-family: 'Outfit', sans-serif; color: #0f172a; font-size: 18px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 16px;">
            Worksheet: ${sh.name}
          </h2>
          <table style="width: 100%; border-collapse: collapse; font-family: 'Outfit', sans-serif; font-size: 12px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <thead>
              <tr style="background: #10B981; color: white;">
                ${sh.headers.map(h => `<th style="padding: 10px 12px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600;">${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${sh.rows.map(row => `
                <tr style="border-bottom: 1px solid #f1f5f9;">
                  ${row.map(cell => `<td style="padding: 8px 12px; color: #334155;">${cell || ''}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    });

    printWindow.document.write(`
      <html>
      <head>
        <title>${workbookTitle}</title>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          body { font-family: 'Outfit', sans-serif; padding: 40px; color: #1e293b; background: white; }
          h1 { color: #10B981; font-size: 26px; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; margin-top: 0; margin-bottom: 30px; font-weight: 600; }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; margin-bottom: 24px; font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em;">
          <span>Workbook PDF Export</span>
          <span>${new Date().toLocaleDateString()}</span>
        </div>
        <h1>${workbookTitle}</h1>
        ${tablesContent}
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 14px',
          borderRadius: 10,
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          border: 'none',
          color: '#FFFFFF',
          fontSize: 12.5,
          fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer',
          boxShadow: '0 4px 10px rgba(16,185,129,0.2)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(16,185,129,0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(16,185,129,0.2)'; }}
      >
        <Download size={14} />
        Download Workbook
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 6,
              width: 190,
              background: '#FFFFFF',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              borderRadius: 12,
              padding: '6px',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.06), 0 8px 10px -6px rgba(0,0,0,0.04)',
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <button
              onClick={handleDownloadExcel}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Download Excel (.xls)
            </button>

            <button
              onClick={handleDownloadPDF}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Download PDF Report
            </button>

            <button
              onClick={handleDownloadWord}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Download Word (.doc)
            </button>

            <button
              onClick={handleDownloadCSV}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Download Active CSV
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function SpreadsheetPanel() {
  const { activePreview, setActivePreview } = useWorkspace();
  const data = activePreview?.data || { title: 'Untitled Workbook', sheets: [] };

  // Local mutable sheets state to allow real-time inline editing and updates
  const [sheets, setSheets] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [editingCell, setEditingCell] = useState(null); // { row, col, value }
  const [editInput, setEditInput] = useState('');

  // Hydrate local state from context whenever activePreview updates
  useEffect(() => {
    if (data.sheets && data.sheets.length > 0) {
      // Format sheets with coordinates and cells
      const formatted = data.sheets.map(sh => {
        // Ensure standard tabular rows format
        const rSource = sh.rows || [];
        const hSource = sh.headers || [];
        return {
          name: sh.name || 'Sheet',
          headers: Array.isArray(hSource) ? hSource.map(String) : [],
          rows: Array.isArray(rSource) ? rSource.map(r => Array.isArray(r) ? r.map(val => String(val !== undefined && val !== null ? val : '')) : []) : []
        };
      });
      setSheets(formatted);
      setActiveSheetIndex(0);
    }
  }, [activePreview]);

  if (!activePreview || sheets.length === 0) return null;

  const currentSheet = sheets[activeSheetIndex] || {};
  const { headers = [], rows = [], name: sheetName = 'Sheet' } = currentSheet;

  // ─── Formula Engine ────────────────────────────────────────────────────────
  // A clean, lightweight formula parser that parses basic mathematical operations
  // and aggregates like SUM(C4:C12) dynamically.
  const evaluateFormula = (expr, currentGrid, visited) => {
    if (!expr || !expr.startsWith('=')) return expr;

    const formula = expr.slice(1).toUpperCase().trim();

    // 1) Evaluate SUM aggregation (e.g., SUM(D4:D15))
    if (formula.startsWith('SUM(') && formula.endsWith(')')) {
      const range = formula.slice(4, -1);
      const parts = range.split(':');
      if (parts.length === 2) {
        const start = parseCellRef(parts[0]);
        const end = parseCellRef(parts[1]);

        if (start && end) {
          let sum = 0;
          const rMin = Math.min(start.row, end.row);
          const rMax = Math.max(start.row, end.row);
          const cMin = Math.min(start.col, end.col);
          const cMax = Math.max(start.col, end.col);

          for (let r = rMin; r <= rMax; r++) {
            for (let c = cMin; c <= cMax; c++) {
              const val = currentGrid[r]?.[c] || '';
              const num = parseFloat(resolveValue(val, currentGrid, visited));
              if (!isNaN(num)) sum += num;
            }
          }
          return sum;
        }
      }
    }

    // 2) Evaluate basic coordinate arithmetic like =D4*F4 or =D4+D5
    const operators = /([\+\-\*\/])/;
    const tokens = formula.split(operators);
    if (tokens.length > 1) {
      try {
        let evalStr = '';
        for (const token of tokens) {
          const trimmed = token.trim();
          if (operators.test(trimmed)) {
            evalStr += trimmed;
          } else {
            const cell = parseCellRef(trimmed);
            if (cell) {
              const rawVal = currentGrid[cell.row]?.[cell.col] || '0';
              const num = parseFloat(resolveValue(rawVal, currentGrid, visited));
              evalStr += isNaN(num) ? '0' : String(num);
            } else {
              evalStr += trimmed; // scalar numbers
            }
          }
        }
        // Use standard Function parser instead of eval for absolute safety and speed
        const result = new Function(`return (${evalStr})`)();
        return typeof result === 'number' && !isNaN(result) ? result : '0';
      } catch (e) {
        return 'ERR!';
      }
    }

    // 3) Single reference like =C4
    const cellRef = parseCellRef(formula);
    if (cellRef) {
      const rawVal = currentGrid[cellRef.row]?.[cellRef.col] || '';
      return resolveValue(rawVal, currentGrid, visited);
    }

    return expr;
  };

  // Helper to resolve cell values recursively while avoiding cyclic infinite loops
  const resolveValue = (val, currentGrid, visited = new Set()) => {
    if (!val || typeof val !== 'string' || !val.startsWith('=')) {
      return val;
    }
    if (visited.has(val)) {
      return 'CYCLE!';
    }
    // Clone visited set to restrict scope of cycle detection to current evaluation branch
    const nextVisited = new Set(visited);
    nextVisited.add(val);
    return String(evaluateFormula(val, currentGrid, nextVisited));
  };

  const getDisplayValue = (val) => {
    const resolved = resolveValue(val, rows);
    const num = parseFloat(resolved);
    
    // Format numeric totals and dollar costing outputs elegantly if applicable
    if (!isNaN(num) && String(resolved).includes('.')) {
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return resolved;
  };

  // ─── Interactive Cell Editing Handlers ─────────────────────────────────────
  const handleCellClick = (rIdx, cIdx, currentRawValue) => {
    setEditingCell({ row: rIdx, col: cIdx });
    setEditInput(currentRawValue);
  };

  const handleSaveCell = () => {
    if (editingCell) {
      const { row, col } = editingCell;
      setSheets(prev => prev.map((sh, sIdx) => {
        if (sIdx !== activeSheetIndex) return sh;
        const nextRows = sh.rows.map((r, rIdx) => {
          if (rIdx !== row) return r;
          return r.map((cVal, cIdx) => cIdx === col ? editInput : cVal);
        });
        return { ...sh, rows: nextRows };
      }));
    }
    setEditingCell(null);
  };

  // ─── Export to Excel CSV ───────────────────────────────────────────────────
  // ─── Export to Native Multi-Sheet Excel ────────────────────────────────────
  const handleDownloadExcel = () => {
    // Escape helper for XML tags
    const escapeXML = (str) => {
      if (str === undefined || str === null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
    };

    let xml = `<?xml version="1.0" encoding="utf-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>DAGGPT</Author>
  <Created>${new Date().toISOString()}</Created>
 </DocumentProperties>
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Bottom"/>
   <Borders/>
   <Font ss:FontName="Segoe UI" x:Family="Swiss" ss:Size="10" ss:Color="#111827"/>
   <Interior/>
   <NumberFormat/>
   <Protection/>
  </Style>
  <Style ss:ID="Header">
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#10B981" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="TotalCell">
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Bold="1" ss:Color="#111827"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E5E7EB"/>
    <Border ss:Position="Bottom" ss:LineStyle="Double" ss:Weight="3" ss:Color="#111827"/>
   </Borders>
  </Style>
 </Styles>`;

    sheets.forEach(sh => {
      // Name length must be <= 31 chars for Excel worksheet compliance
      const safeName = sh.name.replace(/[\[\]\*\?\:\/\\]/g, '').substring(0, 31) || 'Sheet';
      
      xml += `\n <Worksheet ss:Name="${escapeXML(safeName)}">\n  <Table>`;

      // Render headers
      xml += '\n   <Row ss:Height="22">';
      sh.headers.forEach(h => {
        xml += `\n    <Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXML(h)}</Data></Cell>`;
      });
      xml += '\n   </Row>';

      // Render data rows
      sh.rows.forEach(row => {
        // Detect if row is a total row (contains "total" or "profit" in first columns)
        const isTotalRow = row.some(cell => {
          const s = String(cell).toLowerCase();
          return s.includes('total') || s.includes('profit') || s.includes('margin');
        });

        xml += `\n   <Row ss:Height="20">`;

        row.forEach(cell => {
          let type = 'String';
          let val = cell;
          let formulaAttr = '';

          if (typeof cell === 'string' && cell.startsWith('=')) {
            formulaAttr = ` ss:Formula="${escapeXML(cell)}"`;
            // In Excel XML, we can supply a default calculated or parsed number, or omit it
            // Omission is safe because Excel will re-calculate it instantly on file open!
            // But let's also write the local evaluation result as a starting value for immediate compat
            const solved = resolveValue(cell, sh.rows);
            if (solved && !isNaN(Number(solved))) {
              type = 'Number';
              val = solved;
            } else {
              type = 'String';
              val = solved || '0';
            }
          } else if (cell !== '' && !isNaN(Number(cell))) {
            type = 'Number';
            val = Number(cell);
          }

          const cellStyleAttr = isTotalRow ? ' ss:StyleID="TotalCell"' : '';
          xml += `\n    <Cell${cellStyleAttr}${formulaAttr}><Data ss:Type="${type}">${escapeXML(String(val))}</Data></Cell>`;
        });
        xml += '\n   </Row>';
      });

      xml += '\n  </Table>\n </Worksheet>';
    });

    xml += '\n</Workbook>';

    // Download as genuine Excel 2003 XML spreadsheet file (.xls)
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${activePreview.title.replace(/\s+/g, '_')}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 200 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 200 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        borderLeft: `1px solid ${ATELIER.border}`,
        boxShadow: '-8px 0 24px rgba(0,0,0,0.02)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* HEADER SECTION */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: `1px solid ${ATELIER.borderSoft}`,
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(30px)',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#FFFFFF',
            boxShadow: '0 4px 10px rgba(16,185,129,0.2)'
          }}>
            <FileSpreadsheet size={18} />
          </div>
          <div>
            <h3 style={{
              margin: 0, fontSize: 14, fontWeight: 700, color: ATELIER.ink,
              fontFamily: FONTS.sans, letterSpacing: '-0.01em'
            }}>
              {activePreview.title}
            </h3>
            <span style={{ fontSize: 11, color: ATELIER.inkSoft, fontFamily: FONTS.mono }}>
              LIVE WORKSPACE CANVAS
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Action: Download Dropdown */}
          <DownloadDropdown 
            handleDownloadExcel={handleDownloadExcel} 
            sheets={sheets} 
            workbookTitle={activePreview.title} 
            activeSheetIndex={activeSheetIndex} 
          />

          {/* Action: Close */}
          <button
            onClick={() => setActivePreview(null)}
            style={{
              width: 32, height: 32, borderRadius: 10,
              background: ATELIER.bgHover,
              border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: ATELIER.inkMid,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#EF4444'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = ATELIER.bgHover; e.currentTarget.style.color = ATELIER.inkMid; }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* WORKBOOK GRID AREA */}
      <div style={{ flex: 1, overflow: 'auto', position: 'relative', background: '#F8F9FA' }} className="dgw-scroll">
        <table style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          width: '100%',
          fontSize: 13,
          fontFamily: FONTS.sans,
          background: '#FFFFFF',
        }}>
          {/* A-B-C HEADER */}
          <thead>
            <tr>
              <th style={{
                position: 'sticky', top: 0, left: 0, zIndex: 4,
                width: 44, background: '#F3F4F6', color: ATELIER.inkMuted,
                borderBottom: `1px solid ${ATELIER.border}`, borderRight: `1px solid ${ATELIER.border}`,
                textAlign: 'center', fontSize: 11, fontWeight: 700, padding: '8px 0',
              }}>
                /
              </th>
              {headers.map((h, hIdx) => (
                <th key={hIdx} style={{
                  position: 'sticky', top: 0, zIndex: 3,
                  background: '#F3F4F6', color: ATELIER.ink,
                  borderBottom: `1px solid ${ATELIER.border}`, borderRight: `1px solid ${ATELIER.borderSoft}`,
                  padding: '8px 12px', fontSize: 12, fontWeight: 700,
                  textAlign: hIdx === 0 ? 'center' : (h.toLowerCase().includes('cost') || h.toLowerCase().includes('qty') || h.toLowerCase().includes('total') ? 'right' : 'left'),
                  fontFamily: FONTS.mono,
                }}>
                  <div style={{ fontSize: 10, color: ATELIER.inkSoft, marginBottom: 2 }}>{colLetter(hIdx)}</div>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          {/* GRID CELLS */}
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx} style={{ transition: 'background 0.1s' }}>
                {/* 1-2-3 ROW NUMBER INDEX */}
                <td style={{
                  position: 'sticky', left: 0, zIndex: 2,
                  background: '#F3F4F6', color: ATELIER.inkMuted,
                  borderBottom: `1px solid ${ATELIER.borderSoft}`, borderRight: `1px solid ${ATELIER.border}`,
                  textAlign: 'center', fontSize: 11, fontWeight: 700, fontFamily: FONTS.mono,
                  padding: '10px 0', selectNone: 'true',
                }}>
                  {rIdx + 1}
                </td>

                {row.map((cell, cIdx) => {
                  const isEditing = editingCell?.row === rIdx && editingCell?.col === cIdx;
                  const isFormulaVal = cell && String(cell).startsWith('=');
                  const headerLabel = headers[cIdx] || '';
                  const isNumericHeader = headerLabel.toLowerCase().includes('cost') || headerLabel.toLowerCase().includes('qty') || headerLabel.toLowerCase().includes('total') || headerLabel.toLowerCase().includes('unit');

                  return (
                    <td
                      key={cIdx}
                      onDoubleClick={() => handleCellClick(rIdx, cIdx, cell)}
                      style={{
                        padding: '10px 14px',
                        borderBottom: `1px solid ${ATELIER.borderSoft}`,
                        borderRight: `1px solid ${ATELIER.borderSoft}`,
                        textAlign: isNumericHeader ? 'right' : 'left',
                        cursor: 'cell',
                        fontFamily: isNumericHeader || isFormulaVal ? FONTS.mono : FONTS.sans,
                        background: isEditing ? '#EEF2FF' : (isFormulaVal ? '#FBF9FF' : '#FFFFFF'),
                        color: isFormulaVal ? ATELIER.accent : ATELIER.ink,
                        fontWeight: isFormulaVal ? 600 : 400,
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => { if (!isEditing) e.currentTarget.style.background = '#F9FAFB'; }}
                      onMouseLeave={(e) => { if (!isEditing) e.currentTarget.style.background = isFormulaVal ? '#FBF9FF' : '#FFFFFF'; }}
                    >
                      {isEditing ? (
                        <input
                          autoFocus
                          type="text"
                          value={editInput}
                          onChange={(e) => setEditInput(e.target.value)}
                          onBlur={handleSaveCell}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCell();
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                          style={{
                            width: '100%',
                            border: `2px solid ${ATELIER.accent}`,
                            borderRadius: 4,
                            padding: '4px 6px',
                            fontSize: 12.5,
                            fontFamily: FONTS.mono,
                            outline: 'none',
                            background: '#FFFFFF',
                            boxShadow: '0 0 0 3px rgba(124,58,237,0.15)',
                          }}
                        />
                      ) : (
                        getDisplayValue(cell)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CLICKABLE SHEET TABS FOOTER (Circled perfectly in your blueprint!) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        borderTop: `1px solid ${ATELIER.border}`,
        background: '#FFFFFF',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        zIndex: 10,
      }} className="dgw-scroll">
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, fontWeight: 700, color: ATELIER.inkMuted,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          marginRight: 10, borderRight: `1px solid ${ATELIER.borderSoft}`,
          paddingRight: 12, flexShrink: 0
        }}>
          <Grid size={13} style={{ color: '#10B981' }} />
          <span>Workbook Tabs</span>
        </div>

        {sheets.map((sh, idx) => {
          const isActive = idx === activeSheetIndex;
          return (
            <button
              key={idx}
              onClick={() => setActiveSheetIndex(idx)}
              style={{
                flexShrink: 0,
                background: isActive ? '#ECFDF5' : ATELIER.bgHover,
                border: isActive ? '1px solid #10B981' : `1px solid ${ATELIER.borderSoft}`,
                color: isActive ? '#059669' : ATELIER.inkMid,
                fontFamily: FONTS.sans,
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isActive ? '0 2px 6px rgba(16,185,129,0.1)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor = ATELIER.accent;
                  e.currentTarget.style.color = ATELIER.accent;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor = ATELIER.borderSoft;
                  e.currentTarget.style.color = ATELIER.inkMid;
                }
              }}
            >
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: isActive ? '#10B981' : ATELIER.inkFaint
              }} />
              {sh.name}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

export default SpreadsheetPanel;
